/**
 * Browser side of the loop timers: the Blob-URL timer Worker behind `createIntervalScheduler` (hidden tabs throttle
 * main-thread timers; worker timers keep running) and page visibility from `document`.
 */
import {
  createIntervalScheduler,
  type IntervalScheduler,
  type SchedulerDeps,
  type VisibilitySource,
  type WorkerLike,
} from '@hf/client-core/loop/scheduler';

const WORKER_SRC = `
const timers = new Map();
onmessage = (e) => {
  const d = e.data;
  if (d.op === 'start') timers.set(d.id, setInterval(() => postMessage(d.id), d.ms));
  else if (d.op === 'stop') { clearInterval(timers.get(d.id)); timers.delete(d.id); }
};`;

/** The timer worker (null where Workers / Blob URLs are unavailable: tests, Node, strict CSP). */
export const createTimerWorker = (): WorkerLike | null => {
  if (
    typeof Worker === 'undefined' ||
    typeof Blob === 'undefined' ||
    typeof URL?.createObjectURL !== 'function'
  )
    return null;
  try {
    const url = URL.createObjectURL(new Blob([WORKER_SRC], { type: 'text/javascript' }));
    const w = new Worker(url);
    URL.revokeObjectURL(url);
    return w as unknown as WorkerLike;
  } catch {
    return null;
  }
};

/** Worker-backed intervals (setInterval fallback): what the web passes as the match's hidden-tab scheduler. */
export const createBrowserIntervalScheduler = (deps: SchedulerDeps = {}): IntervalScheduler =>
  createIntervalScheduler({ createWorker: createTimerWorker, ...deps });

export const documentVisibility = (): VisibilitySource | null => {
  if (typeof document === 'undefined') return null;
  return {
    get hidden() {
      return document.visibilityState === 'hidden';
    },
    subscribe(fn) {
      document.addEventListener('visibilitychange', fn);
      return () => document.removeEventListener('visibilitychange', fn);
    },
  };
};
